import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusBadge from '../components/StatusBadge';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  CheckIcon, 
  XMarkIcon, 
  EyeIcon,
  CurrencyDollarIcon  // Added this line
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
/*import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusBadge from '../components/StatusBadge';
import { PlusIcon, PencilIcon, TrashIcon, CheckIcon, XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
*/
const Expenses = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [formData, setFormData] = useState({
    category_id: '',
    amount: '',
    description: '',
    expense_date: format(new Date(), 'yyyy-MM-dd'),
    is_salary: false,
    notes: ''
  });
  const [categoryData, setCategoryData] = useState({
    name: '',
    description: ''
  });
  const [filters, setFilters] = useState({
    category_id: '',
    from_date: '',
    to_date: '',
    approved: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 5,
    total: 0
  });

  useEffect(() => {
    fetchExpenses();
    fetchCategories();
  }, [pagination.page, pagination.per_page, filters]);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        ...filters
      };
      // Remove empty filters
      Object.keys(params).forEach(key => 
        params[key] === '' && delete params[key]
      );
      
      const response = await api.get('/expenses/', { params });
      setExpenses(response.data.items || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }));
    } catch (error) {
      toast.error('Failed to fetch expenses');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/expenses/categories');
      setCategories(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleCreateExpense = async () => {
    try {
      await api.post('/expenses/', formData);
      toast.success('Expense created successfully');
      setShowModal(false);
      resetForm();
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create expense');
    }
  };

  const handleUpdateExpense = async () => {
    try {
      await api.put(`/expenses/${selectedExpense.id}`, formData);
      toast.success('Expense updated successfully');
      setShowModal(false);
      resetForm();
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update expense');
    }
  };

  const handleDeleteExpense = async () => {
    try {
      await api.delete(`/expenses/${selectedExpense.id}`);
      toast.success('Expense deleted successfully');
      setShowDeleteDialog(false);
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete expense');
    }
  };

  const handleApproveExpense = async (expense) => {
    try {
      await api.post(`/expenses/${expense.id}/approve`);
      toast.success('Expense approved successfully');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to approve expense');
    }
  };

  const handleCreateCategory = async () => {
    try {
      await api.post('/expenses/categories', categoryData);
      toast.success('Category created successfully');
      setShowCategoryModal(false);
      setCategoryData({ name: '', description: '' });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create category');
    }
  };

  const resetForm = () => {
    setFormData({
      category_id: '',
      amount: '',
      description: '',
      expense_date: format(new Date(), 'yyyy-MM-dd'),
      is_salary: false,
      notes: ''
    });
    setSelectedExpense(null);
  };

  const handleEdit = (expense) => {
    setSelectedExpense(expense);
    setFormData({
      category_id: expense.category_id,
      amount: expense.amount,
      description: expense.description,
      expense_date: expense.expense_date,
      is_salary: expense.is_salary,
      notes: expense.notes || ''
    });
    setShowModal(true);
  };

  const handleView = (expense) => {
    setSelectedExpense(expense);
    setShowViewModal(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      category_id: '',
      from_date: '',
      to_date: '',
      approved: ''
    });
  };

  const getTotalAmount = () => {
    return expenses.reduce((sum, exp) => sum + exp.amount, 0);
  };

  const getApprovedTotal = () => {
    return expenses
      .filter(exp => exp.approved)
      .reduce((sum, exp) => sum + exp.amount, 0);
  };

  const getPendingTotal = () => {
    return expenses
      .filter(exp => !exp.approved)
      .reduce((sum, exp) => sum + exp.amount, 0);
  };

  const columns = [
    {
      header: 'Date',
      accessorKey: 'expense_date',
      cell: ({ getValue }) => format(new Date(getValue()), 'dd MMM yyyy')
    },
    {
      header: 'Expense #',
      accessorKey: 'expense_number',
      cell: ({ getValue }) => (
        <span className="font-medium">{getValue()}</span>
      )
    },
    {
      header: 'Category',
      accessorKey: 'category_name',
    },
    {
      header: 'Description',
      accessorKey: 'description',
      cell: ({ getValue }) => (
        <div className="max-w-xs truncate">{getValue()}</div>
      )
    },
    {
      header: 'Amount',
      accessorKey: 'amount',
      cell: ({ getValue }) => (
        <span className="font-medium">ZMW {getValue()?.toLocaleString() || 0}</span>
      )
    },
    {
      header: 'Type',
      accessorKey: 'is_salary',
      cell: ({ getValue }) => getValue() ? 'Salary' : 'Expense'
    },
    {
      header: 'Status',
      accessorKey: 'approved',
      cell: ({ getValue }) => (
        <StatusBadge status={getValue() ? 'approved' : 'pending'} />
      )
    },
    {
      header: 'Entered By',
      accessorKey: 'entered_by_name',
    },
    {
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleView(row.original)}
            className="text-blue-600 hover:text-blue-900"
            title="View Details"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
          {!row.original.approved && user?.user_type === 'owner' && (
            <button
              onClick={() => handleApproveExpense(row.original)}
              className="text-green-600 hover:text-green-900"
              title="Approve"
            >
              <CheckIcon className="h-5 w-5" />
            </button>
          )}
          {(user?.user_type === 'owner' || !row.original.approved) && (
            <>
              <button
                onClick={() => handleEdit(row.original)}
                className="text-yellow-600 hover:text-yellow-900"
                title="Edit"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => {
                  setSelectedExpense(row.original);
                  setShowDeleteDialog(true);
                }}
                className="text-red-600 hover:text-red-900"
                title="Delete"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      )
    }
  ];

  return (
    <div>
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Expenses</h1>
          <p className="mt-2 text-sm text-gray-700">
            Track and manage business expenses.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex-none space-x-3">
          {(user?.user_type === 'owner' || user?.user_type === 'manager') && (
          <button
            type="button"
            onClick={() => setShowCategoryModal(true)}
            className="btn-secondary"
          >
            Manage Categories
          </button>
          )}
          <button
            type="button"
            onClick={() => navigate('/expenses/create')}
            className="btn-primary"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Expense
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3">
                <CurrencyDollarIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Expenses</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    ZMW {getTotalAmount().toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
                <CheckIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Approved</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    ZMW {getApprovedTotal().toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-yellow-500 rounded-md p-3">
                <XMarkIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    ZMW {getPendingTotal().toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Category</label>
            <select
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              value={filters.category_id}
              onChange={(e) => handleFilterChange('category_id', e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">From Date</label>
            <input
              type="date"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              value={filters.from_date}
              onChange={(e) => handleFilterChange('from_date', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">To Date</label>
            <input
              type="date"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              value={filters.to_date}
              onChange={(e) => handleFilterChange('to_date', e.target.value)}
              min={filters.from_date}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <select
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              value={filters.approved}
              onChange={(e) => handleFilterChange('approved', e.target.value)}
            >
              <option value="">All</option>
              <option value="true">Approved</option>
              <option value="false">Pending</option>
            </select>
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <button
            onClick={clearFilters}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Expenses Table */}
      <div className="mt-8">
        <DataTable
          data={expenses}
          columns={columns}
          loading={loading}
          pageSize={pagination.per_page}
          total={pagination.total}
          page={pagination.page}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          onPageSizeChange={(per_page) => setPagination(prev => ({ ...prev, per_page, page: 1 }))}
        />
      </div>

      {/* View Expense Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedExpense(null);
        }}
        title="Expense Details"
      >
        {selectedExpense && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500">Expense Number</label>
                <p className="font-medium">{selectedExpense.expense_number}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Date</label>
                <p className="font-medium">
                  {format(new Date(selectedExpense.expense_date), 'dd MMM yyyy')}
                </p>
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-500">Category</label>
              <p className="font-medium">{selectedExpense.category_name}</p>
            </div>

            <div>
              <label className="text-sm text-gray-500">Description</label>
              <p className="font-medium">{selectedExpense.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500">Amount</label>
                <p className="font-medium text-lg">
                  ZMW {selectedExpense.amount.toLocaleString()}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Type</label>
                <p className="font-medium">{selectedExpense.is_salary ? 'Salary' : 'Expense'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500">Status</label>
                <div className="mt-1">
                  <StatusBadge status={selectedExpense.approved ? 'approved' : 'pending'} />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-500">Entered By</label>
                <p className="font-medium">{selectedExpense.entered_by_name}</p>
              </div>
            </div>

            {selectedExpense.approved_by && (
              <div>
                <label className="text-sm text-gray-500">Approved By</label>
                <p className="font-medium">{selectedExpense.approved_by}</p>
              </div>
            )}

            {selectedExpense.notes && (
              <div>
                <label className="text-sm text-gray-500">Notes</label>
                <p className="text-gray-700">{selectedExpense.notes}</p>
              </div>
            )}

            {selectedExpense.receipt_url && (
              <div>
                <label className="text-sm text-gray-500">Receipt</label>
                <div className="mt-1">
                  <a
                    href={selectedExpense.receipt_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-800"
                  >
                    View Receipt
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Create/Edit Expense Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={selectedExpense ? 'Edit Expense' : 'Add New Expense'}
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          selectedExpense ? handleUpdateExpense() : handleCreateExpense();
        }}>
          <div className="space-y-4">
            <div>
              <label className="form-label">Category *</label>
              <select
                required
                className="form-input"
                value={formData.category_id}
                onChange={(e) => setFormData({...formData, category_id: e.target.value})}
              >
                <option value="">Select Category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Date *</label>
              <input
                type="date"
                required
                className="form-input"
                value={formData.expense_date}
                onChange={(e) => setFormData({...formData, expense_date: e.target.value})}
                max={format(new Date(), 'yyyy-MM-dd')}
              />
            </div>

            <div>
              <label className="form-label">Amount (ZMW) *</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                required
                className="form-input"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value)})}
              />
            </div>

            <div>
              <label className="form-label">Description *</label>
              <textarea
                required
                className="form-input"
                rows="2"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>

            {user?.user_type === 'owner' && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_salary"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  checked={formData.is_salary}
                  onChange={(e) => setFormData({...formData, is_salary: e.target.checked})}
                />
                <label htmlFor="is_salary" className="ml-2 block text-sm text-gray-900">
                  This is a salary payment
                </label>
              </div>
            )}

            <div>
              <label className="form-label">Notes</label>
              <textarea
                className="form-input"
                rows="2"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                {selectedExpense ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Category Modal */}
      <Modal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setCategoryData({ name: '', description: '' });
        }}
        title="Create Expense Category"
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          handleCreateCategory();
        }}>
          <div className="space-y-4">
            <div>
              <label className="form-label">Category Name *</label>
              <input
                type="text"
                required
                className="form-input"
                value={categoryData.name}
                onChange={(e) => setCategoryData({...categoryData, name: e.target.value})}
              />
            </div>

            <div>
              <label className="form-label">Description</label>
              <textarea
                className="form-input"
                rows="2"
                value={categoryData.description}
                onChange={(e) => setCategoryData({...categoryData, description: e.target.value})}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowCategoryModal(false);
                  setCategoryData({ name: '', description: '' });
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                Create Category
              </button>
            </div>
          </div>
        </form>

        {/* Existing Categories */}
        <div className="mt-6 border-t pt-4">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Existing Categories</h3>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {categories.map(cat => (
              <div key={cat.id} className="flex justify-between items-center text-sm p-2 bg-gray-50 rounded">
                <span className="font-medium">{cat.name}</span>
                <span className="text-gray-500">{cat.description}</span>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteExpense}
        title="Delete Expense"
        message={`Are you sure you want to delete this expense? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
      />
    </div>
  );
};

export default Expenses;